import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { requirementApi, siteApi, safetyKitApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';

const empty = { siteId: '', kitId: '', quantityRequired: '', expectedDate: '', priority: 2, remarks: '' };
const priorityLabel = { 1: '🔴 High', 2: '🟡 Medium', 3: '🟢 Low' };
const ACTIVE_STATUSES = ['open', 'partially_fulfilled'];

// Compute a context-aware label for each of the 8 distinct requirement scenarios
const getReqStatusLabel = (r) => {
  const procs        = r.procurements || [];
  const qtyReceived  = procs.reduce((sum, p) => (p.status === 'received' ? sum + p.quantity : sum), 0);
  const inTransit    = procs.filter((p) => p.status === 'sent').length;

  switch (r.status) {
    case 'open':      return 'Open';
    case 'cancelled': return 'Cancelled';
    case 'received':  return 'Fully Received';
    case 'partially_fulfilled':
      if (qtyReceived === 0)  return 'Partially Fulfilled';
      if (inTransit > 0)      return 'Partially Received';
      return 'Partially Received (Pending Dispatch)';
    case 'fulfilled':
      return qtyReceived === 0 ? 'Fully Dispatched' : 'In Transit';
    default: return r.status?.replace(/_/g, ' ');
  }
};

export default function RequirementList() {
  const { user, hasPermission, isSiteRestricted: isStorekeeper } = useAuth();
  const canCreate = hasPermission('requirement_creation');

  const [reqs, setReqs]       = useState([]);
  // allReqs holds an unfiltered snapshot used for duplicate checks
  const [allReqs, setAllReqs] = useState([]);
  const [sites, setSites]     = useState([]);
  const [kits, setKits]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState({ siteId: '', status: '', kitId: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(empty);
  const [confirm, setConfirm]   = useState(null);
  const [error, setError]       = useState('');
  const navigate = useNavigate();

  // ── Load list ──────────────────────────────────────────────────────────────
  const load = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const params = {};
      if (!isStorekeeper && filter.siteId) params.siteId = filter.siteId;
      if (filter.status) params.status = filter.status;
      if (filter.kitId)  params.kitId  = filter.kitId;
      const [r, s, k] = await Promise.all([
        requirementApi.list(params),
        siteApi.list(),
        safetyKitApi.list(),
      ]);
      setReqs(r.data.data);
      setSites(s.data.data);
      setKits(k.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load requirements');
    } finally {
      setLoading(false);
    }
  };

  // Load unfiltered requirements once for duplicate checking
  const loadAll = async () => {
    try {
      const res = await requirementApi.list({});
      setAllReqs(res.data.data);
    } catch {}
  };

  // Initial load + auto-refresh every 20 s so status changes appear live
  useEffect(() => {
    load(true);                                     // spinner on first load
    const interval = setInterval(() => load(), 20_000);  // silent background ticks
    return () => clearInterval(interval);
  }, [filter]);

  useEffect(() => { loadAll(); }, []);

  // ── Duplicate detection ────────────────────────────────────────────────────
  // Find an existing active requirement for the chosen site + kit
  const duplicateReq = useMemo(() => {
    if (!form.siteId || !form.kitId) return null;
    return allReqs.find(
      (r) =>
        r.siteId === parseInt(form.siteId) &&
        r.kitId  === parseInt(form.kitId)  &&
        ACTIVE_STATUSES.includes(r.status)
    ) ?? null;
  }, [form.siteId, form.kitId, allReqs]);

  // A restricted Storekeeper with no assigned site has nothing valid to submit.
  const noSiteAssigned = isStorekeeper && !user?.siteId;

  // ── Open form ──────────────────────────────────────────────────────────────
  const openForm = () => {
    const defaultSite = isStorekeeper && user?.siteId ? String(user.siteId) : '';
    setForm({ ...empty, siteId: defaultSite });
    setError('');
    setShowForm(true);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = {
        ...form,
        siteId:           parseInt(form.siteId),
        kitId:            parseInt(form.kitId),
        quantityRequired: parseInt(form.quantityRequired),
        priority:         parseInt(form.priority),
        expectedDate:     form.expectedDate || null,
      };
      const res = await requirementApi.create(data);
      setShowForm(false);
      setForm(empty);
      navigate(`/requirements/${res.data.data.id}`);
    } catch (err) { setError(err.response?.data?.message || 'Error creating requirement'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        title="Requirements"
        subtitle="Raise and track safety kit requirements across sites"
        action={canCreate && <button className="btn-primary" onClick={openForm}>+ New Requirement</button>}
      />

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div className="flex gap-3 mb-4 flex-wrap">
        {!isStorekeeper && (
          <div className="w-44">
            <SearchableSelect
              options={[{ id: '', label: 'All Sites' }, ...sites.map((s) => ({ id: s.id, label: s.name }))]}
              value={filter.siteId}
              onChange={(id) => setFilter({ ...filter, siteId: id })}
              placeholder="All Sites"
            />
          </div>
        )}
        <select className="input w-44" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="partially_fulfilled">Partially Fulfilled / Partially Received</option>
          <option value="fulfilled">Fully Dispatched / In Transit</option>
          <option value="received">Fully Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select className="input w-44" value={filter.kitId} onChange={(e) => setFilter({ ...filter, kitId: e.target.value })}>
          <option value="">All Kits</option>
          {kits.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Site</th><th>Kit</th><th>Required</th><th>Fulfilled</th>
              <th>Expected</th><th>Priority</th><th>Status</th>
              {hasPermission('transfer_request') && <th>Suggestions</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reqs.map((r) => (
              <tr key={r.id} className="cursor-pointer" onClick={() => navigate(`/requirements/${r.id}`)}>
                <td className="font-medium">{r.site?.name}</td>
                <td>{r.kit?.name}</td>
                <td className="font-semibold">{r.quantityRequired}</td>
                <td className={r.quantityFulfilled > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}>{r.quantityFulfilled}</td>
                <td className="text-gray-500 text-sm">{r.expectedDate ? new Date(r.expectedDate).toLocaleDateString('en-GB') : '—'}</td>
                <td className="text-sm">{priorityLabel[r.priority]}</td>
                <td><StatusBadge status={r.status} label={getReqStatusLabel(r)} /></td>
                {hasPermission('transfer_request') && (
                  <td>
                    {['open', 'partially_fulfilled'].includes(r.status) && r.suggestions?.length > 0
                      ? <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">{r.suggestions.length} sources</span>
                      : <span className="text-gray-400 text-xs">—</span>
                    }
                  </td>
                )}
                <td onClick={(e) => e.stopPropagation()}>
                  {r.status === 'open' && canCreate && (
                    <button
                      className="text-red-600 hover:underline text-sm"
                      onClick={() => setConfirm({ id: r.id, name: `${r.site?.name} - ${r.kit?.name}` })}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {reqs.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400">No requirements found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Create Modal ─────────────────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Create Requirement</h3>
            <form onSubmit={handleSubmit} className="space-y-4">

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Site</label>
                  {isStorekeeper ? (
                    user?.siteId ? (
                      <input
                        className="input bg-gray-50 cursor-not-allowed"
                        value={sites.find((s) => s.id === user.siteId)?.name || 'Loading your site…'}
                        disabled
                      />
                    ) : (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        No site is assigned to your account yet — contact an administrator before raising a requirement.
                      </p>
                    )
                  ) : (
                    <SearchableSelect
                      options={sites.map((s) => ({ id: s.id, label: s.name }))}
                      value={form.siteId}
                      onChange={(id) => setForm({ ...form, siteId: id })}
                      placeholder="Select site"
                      required
                    />
                  )}
                </div>
                <div>
                  <label className="label">Safety Kit</label>
                  <select
                    className="input"
                    value={form.kitId}
                    onChange={(e) => setForm({ ...form, kitId: e.target.value })}
                    required
                  >
                    <option value="">Select kit</option>
                    {kits.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </select>
                </div>
              </div>

              {/* ── Duplicate warning ────────────────────────────────────────── */}
              {duplicateReq && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-amber-800 mb-1">⚠ Active requirement already exists</p>
                  <p className="text-amber-700">
                    <strong>{duplicateReq.kit?.name}</strong> at <strong>{duplicateReq.site?.name}</strong> has
                    an active requirement{' '}
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={() => { setShowForm(false); navigate(`/requirements/${duplicateReq.id}`); }}
                    >
                      #{duplicateReq.id}
                    </button>
                    {' '}(status: <strong>{duplicateReq.status.replace('_', ' ')}</strong>).
                  </p>
                  <p className="text-amber-600 text-xs mt-1">
                    It must be fulfilled or cancelled before a new one can be raised.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Quantity Required</label>
                  <input
                    className="input"
                    type="number"
                    min="1"
                    value={form.quantityRequired}
                    onChange={(e) => setForm({ ...form, quantityRequired: e.target.value })}
                    required
                    disabled={!!duplicateReq}
                  />
                </div>
                <div>
                  <label className="label">Priority</label>
                  <select
                    className="input"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) })}
                    disabled={!!duplicateReq}
                  >
                    <option value={1}>High</option>
                    <option value={2}>Medium</option>
                    <option value={3}>Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Expected By</label>
                <input
                  className="input"
                  type="date"
                  value={form.expectedDate}
                  onChange={(e) => setForm({ ...form, expectedDate: e.target.value })}
                  disabled={!!duplicateReq}
                />
              </div>

              <div>
                <label className="label">Remarks</label>
                <textarea
                  className="input"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={2}
                  disabled={!!duplicateReq}
                />
              </div>

              {error && <p className="text-red-600 text-sm">{error}</p>}

              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button
                  type="submit"
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!!duplicateReq || noSiteAssigned}
                  title={duplicateReq ? 'Resolve the existing active requirement first' : noSiteAssigned ? 'No site assigned to your account' : ''}
                >
                  Create & Get Suggestions
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete confirm ───────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!confirm}
        title="Delete Requirement"
        message={`Delete requirement for "${confirm?.name}"? This cannot be undone.`}
        onConfirm={async () => {
          try {
            await requirementApi.remove(confirm.id);
            setConfirm(null);
            load();
            loadAll();
          } catch (err) {
            setError(err.response?.data?.message || 'Error deleting requirement');
            setConfirm(null);
          }
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
